import React, { useState, useEffect, useRef } from 'react';
import 'react-international-phone/style.css';

import ConfirmationModal from './ConfirmationModal';
import { sendEvent } from '../../utils/gtag';

const COMPANY_EMAILS = ["info@techvelly.com", "sales@techvelly.com"];

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Only add the listener if no other modal is open on top
      if (!showSuccessModal && !showErrorModal) {
        document.addEventListener('mousedown', handleClickOutside);
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, showSuccessModal, showErrorModal]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.company) newErrors.company = 'Company Name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.message) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      sendEvent('contact_us_submit', { source: 'header_modal' });
      setShowSuccessModal(true);
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });

    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
      setErrorMessage("Submission failed. Please try again later.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div ref={modalRef} className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full relative transform transition-all duration-300 scale-100 opacity-100">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            &times;
          </button>
          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Contact Us</h2>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <label htmlFor="name" className="block text-gray-600 text-xs font-bold mb-1">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-transparent border-b border-gray-400/50 placeholder-gray-400 text-gray-700 py-1 px-0 focus:outline-none focus:border-blue-500 w-full text-sm"
              />
              {errors.name && <p className="text-red-500 text-xs italic">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="company" className="block text-gray-600 text-xs font-bold mb-1">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="bg-transparent border-b border-gray-400/50 placeholder-gray-400 text-gray-700 py-1 px-0 focus:outline-none focus:border-blue-500 w-full text-sm"
              />
              {errors.company && <p className="text-red-500 text-xs italic">{errors.company}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-gray-600 text-xs font-bold mb-1">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="bg-transparent border-b border-gray-400/50 placeholder-gray-400 text-gray-700 py-1 px-0 focus:outline-none focus:border-blue-500 w-full text-sm"
              />
              {errors.email && <p className="text-red-500 text-xs italic">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="phone" className="block text-gray-600 text-xs font-bold mb-1">Phone</label>
              <input
                type="phone"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="bg-transparent border-b border-gray-400/50 placeholder-gray-400 text-gray-700 py-1 px-0 focus:outline-none focus:border-blue-500 w-full text-sm"
              />
              {errors.phone && <p className="text-red-500 text-xs italic">{errors.phone}</p>}
            </div>
            <div>
              <label htmlFor="message" className="block text-gray-600 text-xs font-bold mb-1">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
                className="bg-transparent border-b border-gray-400/50 placeholder-gray-400 text-gray-700 py-1 px-0 focus:outline-none focus:border-blue-500 w-full text-sm"
              ></textarea>
              {errors.message && <p className="text-red-500 text-xs italic">{errors.message}</p>}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              onClick={() => sendEvent('contact_us_click', { source: 'header_modal' })}
              className="mt-6 w-full inline-block rounded-lg bg-blue-600 px-6 py-2 text-center text-base font-semibold text-white no-underline shadow-lg shadow-blue-500/40 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        title="Success!"
        message="Your message has been sent successfully."
        type="success"
      />
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage("");
        }}
        title="Submission Failed"
        message={errorMessage}
        type="error"
        companyEmails={COMPANY_EMAILS}
      />
    </>
  );
};

export default ContactFormModal;